import React from 'react';
import _, {entries} from 'lodash';
import {Link, useParams} from "react-router-dom";
import Button from "react-bootstrap/Button";
import moment from "moment";
import {concepts, explanations, SubscriptionRule} from "fearless-common";
import {FloatingLabelField} from "fearless-common-ui";
import {useField} from "formik";
import {FormText, Row, Col } from "react-bootstrap";
import {BoxArrowUpRight} from "react-bootstrap-icons";
import useGet from "../../../components/hooks/useGet";
import {BackendForm} from "../../../components/forms";
import {Pages} from "../../../components/layout";
import {defaultDateTimeFormat} from "../../../utils/date";
import {adminRoutes, newString} from "../../../utils/routes";
import {backendEndpoints} from "../../../libs/backendEndpoints";
import SubscriptionRuleDeleteButton from "../../../components/subscriptions/SubscriptionRuleDeleteButton";
import BackendSelect from "../../../components/forms/BackendSelect";
import SubscriptionRuleCustomFieldsBuilder from "./SubscriptionRuleCustomFieldsBuilder";
import AdminNotes from "../../../components/admin/AdminNotes";
import {standardDateFormat} from "../../../utils/momentHelpers";
import useTitle from "../../../components/hooks/useTitle";


const typeName = 'type';
const timeTypeName = 'time_type';

const Types: Record<SubscriptionRule['type'], { valueHeader: string; explanation: string}> = {
    email_domain: {
        valueHeader: 'Full email domain (everything that comes after \'@\')',
        explanation: 'Give all people with a certain email domain name access to subscription features.',
    },
    temporary_group_access: {
        valueHeader: 'Group ID',
        explanation: 'Give a group temporary access to subscription features.',
    },
    temporary_user_access: {
        valueHeader: 'User ID',
        explanation: 'Give a user temporary access to subscription features.',
    },
}

function getExplanation(timeTypeValue: string | null) {
    if (timeTypeValue === '') {
        return 'Rule only applies within above date range.';
    }
    if (timeTypeValue === 'time_after_sign_up') {
        return 'Users have access N months after sign up when having signed up between the above dates.';
    }
    return '-';
}

const defaultEndDate = moment().add(2, 'years').format(standardDateFormat);

function ValueField({ name }: { name: string }) {
    const [field] = useField<SubscriptionRule['type']>(name);
    return (
        <FloatingLabelField
            name="value"
            label={field.value in Types ? Types[field.value].valueHeader : 'Value'}
            required
        />
    );
}

function RuleTypeExplanations() {
    const [{ value }] = useField<SubscriptionRule['type']>(typeName);
    return (
        <FormText className="text-sm ps-4 d-block">
            {value in Types ? Types[value].explanation : '-'}
        </FormText>
    );
}

function RuleTimeTypeExplanations() {
    const [{ value }] = useField<SubscriptionRule['time_type_value']>(timeTypeName);
    const [{ value: type }] = useField(typeName);

    const emailDomain = type === 'email_domain';

    return emailDomain ? (
        <>
            <BackendSelect<Record<string, string>>
                label="Applies at"
                name={timeTypeName}
                className="mb-0"
                endpoint={backendEndpoints.adminSubscriptionRuleTimeTypes}
                resultProcessor={(types) => {
                    return Object.keys(types || {}).map((k) => ({value: k, label: types[k]}));
                }}
            />
            <FormText className="text-sm ps-4 d-block">{getExplanation(value)}</FormText>
            <div className="mb-2" />
            {value === 'time_after_sign_up' && (
                <FloatingLabelField type="text" name="time_type_value" label="Number of months after sign up"/>
            )}
        </>
    ) : null;
}

export default function AdminSubscriptionRule() {
    const { ruleId } = useParams<{ ruleId: string }>();
    const isNew = ruleId === newString;
    const { data: rule} = useGet<SubscriptionRule>({
        endpoint: isNew || !ruleId ? '' : backendEndpoints.adminSubscriptionRule(ruleId),
    });

    useTitle(`Admin ${concepts.SubscriptionRule}`);

    return (
        <Pages.Centred>
            <Button href={adminRoutes.subscriptionRules}>Back to rules</Button>
            {isNew ? <h1>New Rule</h1> : <h1>Rule #{rule?.id || '...'}</h1>}
            <div className="d-flex gap-4 mb-3">
                <span>Created at: {moment(rule?.created_at).format(defaultDateTimeFormat)}</span>
            </div>
            {(isNew || rule) && (
                <>
                    <Row>
                        <Col sm={4}>
                            <BackendForm<Pick<SubscriptionRule, 'type' | 'max_applications' | 'signup_custom_fields' | 'time_type' | 'end_date' | 'start_date' | 'time_type_value'>, { id: number }>
                                initialValues={isNew || !rule ? {
                                    type: 'email_domain',
                                    max_applications: 300,
                                    signup_custom_fields: {},
                                    time_type: null,
                                    end_date: defaultEndDate,
                                    start_date: '',
                                    time_type_value: '',
                                } : {
                                    ..._.omit(rule, ['users']),
                                    end_date: rule.end_date || defaultEndDate,
                                }}
                                targetEndpoint={isNew || !ruleId ? backendEndpoints.addSubscriptionRule : backendEndpoints.subscriptionRule(ruleId)}
                                targetMethod={isNew ? 'POST' : "PUT"}
                                onSuccess={(resp) => {
                                    if (resp.data) {
                                        if (isNew) {
                                            window.location.href = resp.data?.id
                                                ? adminRoutes.subscriptionRule(resp.data.id)
                                                : adminRoutes.subscriptionRules;
                                        } else {
                                            // If successful, reload page to show the new list of users.
                                            window.location.reload();
                                        }
                                    }
                                }}
                            >
                                <BackendSelect<Record<string, string>>
                                    endpoint={backendEndpoints.adminSubscriptionRuleProducts}
                                    name="product"
                                    label="StripeProduct"
                                    resultProcessor={(types) => {
                                        return entries(types).map(([k, v]) => ({value: k, label: v}));
                                    }}
                                />
                                <FloatingLabelField name="title" label="Title"/>
                                <FloatingLabelField name="description" label="Description" type="textarea"/>
                                <BackendSelect
                                    endpoint={backendEndpoints.getAdminInstitutes}
                                    name="institute_id"
                                    label="Institute"
                                    placeholder="----"
                                />
                                <div className="mb-5"></div>
                                <BackendSelect<Record<string, string>>
                                    label="Rule type"
                                    name={typeName}
                                    className="mb-0"
                                    endpoint={backendEndpoints.adminSubscriptionRuleTypes}
                                    resultProcessor={(types) => {
                                        return entries(types).map(([k, v]) => ({value: k, label: v}));
                                    }}
                                    required
                                />
                                <RuleTypeExplanations />
                                <div className="mb-2" />
                                <ValueField name={typeName}/>

                                <div className="mb-5" />
                                <FloatingLabelField
                                    name="max_applications"
                                    label="Maximum applications"
                                    type="number"
                                    required
                                />
                                <FloatingLabelField
                                    name="start_date"
                                    label="Start date (yyyy-mm-dd)"
                                    type="date"
                                    required
                                />
                                <FloatingLabelField
                                    name="end_date"
                                    label="End date (yyyy-mm-dd)"
                                    type="date"
                                    required
                                />
                                <RuleTimeTypeExplanations />
                                <h5 className="mt-5">Custom sign-up fields</h5>
                                <SubscriptionRuleCustomFieldsBuilder />
                            </BackendForm>
                        </Col>
                        <Col>
                            {rule && (
                                <>
                                    <h5>{rule?.users?.length} {concepts.Users.toLowerCase()}</h5>
                                    <p>{concepts.Users} accounts that fall under this rule:</p>
                                    <ul>
                                        {rule?.users?.map(({email, id}) => (
                                            <li key={id}>
                                                {email} <Link to={adminRoutes.user(id)}><BoxArrowUpRight /></Link>
                                            </li>
                                        ))}
                                    </ul>
                                    <hr/>
                                    <h5>Archive {concepts.SubscriptionRule}</h5>
                                    <SubscriptionRuleDeleteButton rule={rule}/>
                                    <hr/>
                                    <h5>{concepts.AdminNotes}</h5>
                                    <p>{explanations.WhatAreAdminNotes}</p>
                                    <AdminNotes
                                        type="App\Models\SubscriptionRule"
                                        typeId={rule.id}
                                    />
                                </>
                            )}

                        </Col>
                    </Row>
                </>
            )}

        </Pages.Centred>
    );
}
